<template>
  <div class="d-flex flex-column page-wrapper welcome">
    <page-header v-if="!uploading">Welcome to <strong> {{ creditUnionName }}</strong></page-header>
    <div class="container d-flex flex-column align-items-center page-container" v-if="!isIpAllowed">
      <span v-html="ipRestrictedMessage"></span>
    </div>
    <div class="container d-flex flex-column align-items-center page-container" v-if="!uploading && !uploadFailed && uploadSuccess && isIpAllowed">
      <div class="d-flex flex-column explanation-text">
        <p>
          We are so glad you've decided to join us. Just a few things you'll need before we get 
          started. <strong>Please note that unless otherwise indicated, most fields in this form are required.</strong>
        </p>
        <div class="d-flex flex-column align-self-center checkmark-list">
          <div class="d-flex flex-row justify-content-start align-items-start checkmark-item">
            <font-awesome-icon icon="check-circle" class="checkmark-icon"></font-awesome-icon>
            <p class="checkmark-text">A valid government issued ID</p>
          </div>
          <div class="d-flex flex-row justify-content-start align-items-start checkmark-item">
            <font-awesome-icon icon="check-circle" class="checkmark-icon"></font-awesome-icon>
            <p class="checkmark-text">A valid email address</p>
          </div>
          <div class="d-flex flex-row justify-content-start align-items-start checkmark-item">
            <font-awesome-icon icon="check-circle" class="checkmark-icon"></font-awesome-icon>
            <p class="checkmark-text">Social Security Number</p>
          </div>
          <div class="d-flex flex-row justify-content-start align-items-start checkmark-item">
            <font-awesome-icon icon="check-circle" class="checkmark-icon"></font-awesome-icon>
            <p class="checkmark-text">{{ primarySavingsAccountAmount | currency }} to fund a Primary Savings Account</p>
          </div>
          <div class="d-flex flex-row justify-content-start align-items-start checkmark-item">
            <font-awesome-icon icon="check-circle" class="checkmark-icon"></font-awesome-icon>
            <p class="checkmark-text">Make sure you live, work, go to school, and/or worship in our <a href="https://www.ascentra.org/belong/join/become-a-member" target="_blank"><strong>membership area</strong></a></p>
          </div>
          <div class="d-flex flex-row justify-content-start align-items-start checkmark-item">
            <font-awesome-icon icon="check-circle" class="checkmark-icon"></font-awesome-icon>
            <p class="checkmark-text">Read and accept the membership disclosure before starting your application</p>
          </div>
        </div>
      </div>
      <div class="d-flex flex-column justify-content-start align-items-start form-container">
        <b-form @submit.prevent="readDisclosure" novalidate>
          <b-form-group class="form-label" label="Would you like to add a Joint Applicant to this account?">
              <b-button-group vertical class="btn-width" :id="$id('applicationType')">                        
                  <b-button variant="outline-primary" :pressed="option.value === jointOwner" @click="selectjointOwner(option)" v-for="option in jointOwnerOptions" :key="option.value">{{ option.name }}</b-button>
              </b-button-group>
              <div class="field-error-message" v-if="!$v.jointOwner.required && $v.jointOwner.$dirty">Please select if you'd like to add a joint owner</div>
          </b-form-group>
          <p v-if="jointOwner === true">
              <strong>This is important!</strong><br> The Joint Applicant must be present for the application process. 
              In addition, the Joint Applicant agrees to all disclosures included in the application process. They will also have access to make transactions on your account.
          </p>
          <b-form-group class="form-label" label="Promo Code (if you have one)">
              <b-form-input type="text" :id="$id('promoCode')" v-model="promoCode"/>        
          </b-form-group>
          
          <!-- <ejs-datepicker :placeholder="'Select a date'" :enableMask='true'></ejs-datepicker> -->
          <div class="d-flex flex-column justify-content-between align-items-center final-action-div">
            <b-button variant="secondary" type="submit" class="btn btn-width" :id="$id('next')" :disabled="jointOwner === null">Read &amp; Accept Membership Disclosure</b-button>
            <!-- <ejs-button type='submit'>Read &amp; Accept Membership Disclosure</ejs-button> -->
          </div>
        </b-form>
      </div>
    </div>
    <div class="container d-flex flex-column align-items-center justify-content-center flex-grow-1" v-if="uploading">
      <p class="processing-text">Please wait while we get your application ready</p>
      <img src="@/assets/img/light/activity.gif" class="processing-img" />
    </div>
    <div class="container d-flex flex-column align-items-center page-container" v-if="!uploading && uploadFailed">
      <p class="processing-text">
        Oops! It seems like this application isn't available right now. Please check back later or  for immediate help, reach out to one of our friendly team members at (563) 355-0152. We're here to help!        
      </p>
    </div>
    <page-footer :class="{ 'hidden': uploading }" />
    <b-modal ref="disclosureModalRef" id="disclosureModal" size="lg" scrollable body-text-variant="dark" header-text-variant="dark" centered title="Membership Disclosure">
      <span id="disclosure" v-html="agreementText"></span>
      <div class="d-flex flex-column justify-content-between align-items-center margin-top-40 margin-bottom-15">
          <b-button variant="primary" @click="email()" class="btn width-240">
              <span><font-awesome-icon icon="envelope"/></span> {{ $t('message.emailAgreement') }}
          </b-button>        
          <div class="height-10"></div>  
          <b-button variant="primary" @click="print()" class="btn width-240">
              <span><font-awesome-icon icon="print"/></span> {{ $t('message.printAgreement') }}
          </b-button>                      
      </div>
      <div slot="modal-footer" class="modal-footer">
          <b-button variant="outline-secondary" class="modal-btn" @click="cancel">{{ $t('message.cancel') }}</b-button>
          <b-button variant="secondary" class="modal-btn" @click="acceptDisclosure">{{ $t('message.acceptDisclosure') }}</b-button>
      </div>
    </b-modal>
    <b-modal ref="emailModal" body-text-variant="dark" header-text-variant="dark" centered>
        Please enter the email address you wish to send this agreement to            
        <b-form @submit.prevent="sendEmail" novalidate class="form-container">
          <b-form-group class="form-label" label="Email Address">
            <b-form-input type="email" :id="$id('emailAddress')" v-model="emailAddress" />           
            <div class="field-error-message" v-if="!$v.emailAddress.required && $v.emailAddress.$dirty">You must provide a valid Email Address</div>        
            <div class="field-error-message" v-if="!$v.emailAddress.email && $v.emailAddress.$dirty">Please enter a valid Email Address</div>        
          </b-form-group>
        </b-form>
        <div slot="modal-footer">
            <b-button variant="secondary" @click="sendEmail()">Send Email</b-button>
        </div>
    </b-modal>
    <b-modal ref="invalidEmailModalRef" body-text-variant="dark" header-text-variant="dark" centered ok-only ok-variant="secondary" title="Error Sending Email">
        We were unable to send the email, please confirm your email address and retry
    </b-modal>
    <b-modal ref="emailSentModalRef" body-text-variant="dark" header-text-variant="dark" centered ok-only ok-variant="secondary" title="Email Sent">
        The agreement has been sent to {{ emailAddress }}
    </b-modal>
    <b-modal ref="errorModalRef" body-text-variant="dark" header-text-variant="dark" centered ok-only ok-variant="secondary" title="Missing Information">
      {{ $t('message.missingRequiredFieldsMsg') }}
    </b-modal>
  </div>
</template>

<script>
import { email, required } from "vuelidate/lib/validators";
import { mapState, mapGetters, mapMutations, mapActions } from "vuex";
import Vue from "vue";
import VueGtm from '@gtm-support/vue2-gtm';

export default {
  name: "Welcome",
  created() {
    this.uploading = true;
    let self = this;
    this.getAppConfig().then((config) => {      
      if(config) {        
        this.getPortalContent().then(async (success) => {
          if(success) {
            let headerContents = this.getPageContent('Header');
            let gtmId = headerContents.find(content => content.contentName === 'GTMID');
            console.info('gtm id', gtmId);
            if(gtmId) {
              Vue.use(VueGtm, {
                id: gtmId.content,
                enabled:true,                
                vueRouter: this.$router,
              });
            } 
            this.pageContents = this.getPageContent('Welcome');
            let isValidIp = true;
            if(this.ipCheckRequired) { 
              //isValidIp = await this.ipallowedCheck();
            }            

            // CE-255 Let's use vue-head to add external scripts from the config DB
            let configuredScripts = this.getPageContent('ExternalScripts');
            if(configuredScripts) {
              for(let i = 0; i < configuredScripts.length; i++) {
                let script = configuredScripts[i];
                console.info('script found', script);
                let record = {
                  type: 'text/javascript',
                  src: script.content,
                  async: true,
                  body: script.languageCode === 'BODY'
                };

                self.scripts.push(record);
              }
            }

            // This is for vue-head to update the header once we have the config
            self.$emit('updateHead');
            
            if(isValidIp) {
              this.initApp(config);              
              this.uploadFailed = false;
              this.uploadSuccess = true;
            } else {
              this.isIpAllowed = false;              
              this.$gtm.trackEvent({
                event: null, // Event type [default = 'interaction'] (Optional)
                category: 'Security',
                action: 'submit',
                label: 'IP Allowed Check Failed',
                value: 5000,
                noninteraction: true, // Optional
            });
            }
          } else {
            this.uploadFailed = true;
            this.uploadSuccess = false;
          }
          this.uploading = false;
        });
      } else {
        this.uploadFailed = true;
        this.uploadSuccess = false;
        this.uploading = false;
      }
    });
  },
  data: function () {
    return {
      pageContents: [],
      uploading: false,
      uploadFailed: false,
      uploadSuccess: false,
      disclosureAccepted: false,
      jointOwnerOptions: [
        { id: '1', name: 'Yes', value: true },
        { id: '2', name: 'No', value: false },
      ],
      primarySavingsAccountAmount: 5.00,
      isIpAllowed: true,  
      scripts: [],
    };
  },
  head: {
    title: function() {
      return { 
        inner: 'Apply for Membership',
        complement: this.creditUnionName
      }
    },
    meta: [
      { name: 'application-name', content: 'Consumer Gravitation - New Consumer'}
    ],
    script: function() {
        return this.scripts;
    }
  },
  computed: {
    ...mapState([
      "ipCheckRequired"
    ]),
    ...mapGetters([
      'getPageContent'
    ]),
    creditUnionName: {
      get() {
        return this.$store.state.creditUnion.name;
      }
    },
    creditUnionPhone: {
      get() {
        return this.$store.state.creditUnion.phoneNumber;
      }
    },
    eligibilityRequirementsUrl: {
      get() {
        return this.$store.state.eligibilityRequirementsUrl;
      }
    },
    agreementText: {
      get() {
        var text = this.pageContents.find(content => content.contentName === 'Disclosure');
        return text ? text.content : '';
      }
    },
    ipRestrictedMessage: {
      get() {        
        var text = this.pageContents.find(content => content.contentName === 'IP Restricted Message');
        return text ? text.content : '';
      }
    },
    jointOwner: {
      get() {
        return this.$store.state.isJointApplication;
      },
      set(value) {
        this.updateIsJointApplication(value);
      }
    },
    promoCode: {
      get() {
        return this.$store.state.promoCode;
      },
      set(value) {
        this.updatePromoCode({ promoCode: value });
      }
    },
    emailAddress: {
      get() {
        return this.$store.state.primaryEmail.emailAddress;
      },
      set(value) {
        this.updateApplicantEmail({ isPrimary: true, emailAddress: value });
      }
    },
  },
  methods: {
    ...mapActions([
      'getAppConfig',
      'getPortalContent',
      'ipallowedCheck',
      'sendEsignAgreement'
    ]),
    ...mapMutations([
      'updateIsJointApplication',
      'updatePromoCode',
      'updateApplicantEmail'
    ]),
    initApp: function(config) {
    },
    selectjointOwner: function(opt) {
      this.jointOwner = opt.value;
    },
    readDisclosure: function() {
      this.$v.$touch();
      if(this.$v.jointOwner.$invalid) {
        this.$refs.errorModalRef.show();
      } else {
        this.$refs.disclosureModalRef.show();
      }
    },
    acceptDisclosure: function () {
      this.disclosureAccepted = true;
      // this.$router.push("verification");
      this.$router.push("identification");
    },
    cancel: function () {
      this.$refs.disclosureModalRef.hide();
    },      
    print: function() {
      this.$htmlToPaper('disclosure', null, () => {
        console.log('Printing completed or was cancelled');
      });
    },
    email: function() {
      this.$v.$reset();
      this.$refs.emailModal.show();
    },
    sendEmail: async function() {
      this.$v.$touch();
      if (this.$v.emailAddress.$invalid) {
        return;
      } else {
        let success = await this.sendEsignAgreement(this.emailAddress);
        if(success) {        
          this.$refs.emailModal.hide();
          this.$refs.emailSentModalRef.show();                
        } else {
          this.$refs.invalidEmailModalRef.show();
        }
      }
    }
  },
  validations: {
    jointOwner: {
      required,
    },
    emailAddress: {          
      email,
      required
    },
  }
};
</script>
